// Utilitare mici folosite peste tot: event bus, formatare, DOM.

/** Event bus minimal. */
export class Bus {
  #handlers = new Map();

  on(event, fn) {
    if (!this.#handlers.has(event)) this.#handlers.set(event, new Set());
    this.#handlers.get(event).add(fn);
    return () => this.off(event, fn);
  }

  off(event, fn) {
    this.#handlers.get(event)?.delete(fn);
  }

  emit(event, payload) {
    const set = this.#handlers.get(event);
    if (!set) return;
    for (const fn of [...set]) {
      try { fn(payload); } catch (err) { console.error(`[bus:${event}]`, err); }
    }
  }
}

export const bus = new Bus();

// ------------------------------------------------------------------ format

/** 6623468767 -> "6.6B$", 2103000 -> "2.1M$", 95000 -> "95K$" */
export function fmtVolume(value, suffix = '$') {
  const v = Math.abs(Number(value) || 0);
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B${suffix}`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(0)}M${suffix}`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K${suffix}`;
  return `${v.toFixed(0)}${suffix}`;
}

/** 2103000 -> "2.1M" (fără sufix monetar — pentru numărul de tranzacții) */
export function fmtCount(value) {
  const v = Math.abs(Number(value) || 0);
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return String(Math.round(v));
}

export function fmtPct(value, digits = 2) {
  const v = Number(value) || 0;
  return `${v >= 0 ? '+' : ''}${v.toFixed(digits)}%`;
}

/** Numărul de zecimale potrivit pentru un preț: 64940 -> 2, 0.0028266 -> 7. */
export function priceDigits(price) {
  const p = Math.abs(Number(price) || 0);
  if (p >= 1000) return 2;
  if (p >= 100) return 3;
  if (p >= 1) return 4;
  if (p >= 0.01) return 5;
  if (p >= 0.0001) return 6;
  return 8;
}

export function fmtPrice(price, digits = null) {
  const d = digits ?? priceDigits(price);
  return (Number(price) || 0).toFixed(d);
}

/** 3660 -> "01:01" (folosit pentru countdown-ul de funding) */
export function fmtDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return h > 0
    ? `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/** 45 -> "45с", 90 -> "2м", 3600 -> "1ч" — durata de viață a unei densități. */
export function fmtAge(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}с`;
  if (s < 3600) return `${Math.floor(s / 60)}м`;
  if (s < 86400) return `${Math.floor(s / 3600)}ч`;
  return `${Math.floor(s / 86400)}д`;
}

/** "BTCUSDT" -> { base: "BTC", quote: "USDT" } */
export function splitSymbol(symbol) {
  for (const quote of ['USDT', 'USDC', 'USD', 'BTC', 'ETH', 'BNB', 'EUR', 'TRY']) {
    if (symbol.endsWith(quote) && symbol.length > quote.length) {
      return { base: symbol.slice(0, -quote.length), quote };
    }
  }
  return { base: symbol, quote: '' };
}

// --------------------------------------------------------------------- DOM

/** el('div', { class: 'x', onclick: fn }, [children]) */
export function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class') node.className = v;
    else if (k === 'html') node.innerHTML = v;
    else if (k === 'text') node.textContent = v;
    else if (k === 'style' && typeof v === 'object') Object.assign(node.style, v);
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
    else if (k === 'dataset') Object.assign(node.dataset, v);
    else node.setAttribute(k, v === true ? '' : v);
  }
  for (const child of [].concat(children)) {
    if (child === null || child === undefined || child === false) continue;
    node.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return node;
}

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

export function clear(node) {
  while (node.firstChild) node.firstChild.remove();
  return node;
}

// ------------------------------------------------------------------ timing

export function debounce(fn, ms = 200) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

export function throttle(fn, ms = 100) {
  let last = 0, pending = null;
  return (...args) => {
    const now = Date.now();
    if (now - last >= ms) { last = now; fn(...args); }
    else {
      clearTimeout(pending);
      pending = setTimeout(() => { last = Date.now(); fn(...args); }, ms - (now - last));
    }
  };
}

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Durata unui timeframe, în secunde. */
export const TF_SECONDS = {
  '1m': 60, '3m': 180, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '2h': 7200, '4h': 14400, '6h': 21600, '12h': 43200,
  '1d': 86400, '3d': 259200, '1w': 604800,
};

/** Eticheta rusească a unui timeframe: 5m -> "5м", 1h -> "1ч", 1d -> "1д". */
export function tfLabel(tf) {
  return tf.replace('m', 'м').replace('h', 'ч').replace('d', 'д').replace('w', 'н');
}
